import { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useParams } from 'react-router';
import { Icon, Text, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { Select } from '~/components/Select';
import { AccordionBase } from '~/components/Accordion';
import { BehaviorChartView, CONTROLS_HEIGHT } from '~/components/BehaviorChartView';
import { useAdminTimeRange, TIME_RANGE_OPTIONS, type TimeRange } from '~/hooks/useAdminTimeRange';
import { useStudentBehaviorReportQuery } from '~/services/administrator/useStudentBehaviorReportQuery';
import { useStudentSummaryQuery } from '~/services/administrator/useStudentSummaryQuery';
import { useSchoolStudentQuery } from '~/services/administrator/useSchoolStudentQuery';

import { AdminLayout } from '~/layouts/AdminLayout';

/** Bounds the summary card so the loading spinner has somewhere to sit and the
 *  layout doesn't jump when the markdown lands. Matches the Figma comp's
 *  inner content region. */
const SUMMARY_MIN_HEIGHT = 110;
const STUDENT_NAME_FALLBACK = '—';

export function StudentDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { timeRange, setTimeRange, timeRangeLabel } = useAdminTimeRange();
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);

  const { data, status: studentStatus } = useSchoolStudentQuery({ slug });
  const studentDisplayName = data?.voiceName ?? STUDENT_NAME_FALLBACK;
  // Title is just the student's name; class + teacher are shown de-emphasized on the far right.
  const headerMeta = data ? `${data.periodName} ${data.periodShortName} - ${data.teacherName}` : undefined;

  const queriesEnabled = data !== undefined;
  const { data: reportData, status: reportStatus } = useStudentBehaviorReportQuery({
    periodId: data?.periodId ?? '',
    studentId: data?.studentId ?? '',
    timeRange,
    enabled: queriesEnabled,
  });

  const { data: summaryData, status: summaryStatus } = useStudentSummaryQuery({
    periodId: data?.periodId ?? '',
    studentId: data?.studentId ?? '',
    timeRange,
    enabled: queriesEnabled && !summaryCollapsed,
  });

  const summaryMarkdown = summaryData?.summary.trim() ?? '';
  const summaryContent = (
    <View style={styles.summaryContent}>
      {summaryStatus === 'pending' && <Loading backgroundColor="transparent" />}
      {summaryStatus === 'error' && <ErrorBox text="Failed to load summary." />}
      {summaryStatus === 'success' &&
        (summaryMarkdown.length > 0 ? (
          <Text style={styles.summaryText}>{summaryMarkdown}</Text>
        ) : (
          <Text style={[styles.summaryText, styles.summaryEmpty]}>No summary to show, yet</Text>
        ))}
    </View>
  );

  if (studentStatus === 'error') {
    return (
      <AdminLayout
        parentCrumb={{ label: 'Students', to: '/students' }}
        titleSegments={[STUDENT_NAME_FALLBACK]}
        showSchoolName={false}
        scrollable={false}>
        <ErrorBox text="Student not found." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      parentCrumb={{ label: 'Students', to: '/students' }}
      titleSegments={[studentDisplayName]}
      headerMeta={headerMeta}
      showSchoolName={false}
      scrollable={false}>
      <View style={styles.chartContainer}>
        <BehaviorChartView
          data={reportData}
          loading={reportStatus === 'pending'}
          hasError={reportStatus === 'error'}
          errorMessage="Failed to load student data. Please try reloading."
          title="Student Behavior"
          absentAsBaseline={true}
          controlsLayout="stacked"
          controls={
            <Select
              value={timeRange}
              onChange={(v) => setTimeRange(v as TimeRange)}
              options={TIME_RANGE_OPTIONS}
              height={CONTROLS_HEIGHT}
            />
          }
        />
      </View>

      <AccordionBase
        title={`${studentDisplayName}'s summary (${timeRangeLabel})`}
        icon={<Icon type="document" />}
        content={summaryContent}
        isCollapsed={summaryCollapsed}
        onToggleCollapse={setSummaryCollapsed}
        noContentBorder={true}
        containerStyle={styles.summaryContainer}
      />
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  chartContainer: {
    flex: 1,
    width: '100%',
    marginBottom: theme.spacing.xlarge,
  },
  summaryContainer: {
    backgroundColor: theme.colors.gray,
    borderWidth: 0,
  },
  summaryContent: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.small,
    padding: theme.spacing.large,
    minHeight: SUMMARY_MIN_HEIGHT,
  },
  summaryText: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.navy,
    lineHeight: theme.lineHeights.small,
  },
  summaryEmpty: {
    fontStyle: 'italic',
    color: theme.colors.darkGray,
  },
});
